import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createDocumentFolder,
  getDownloadUrlDoc,
  listDocumentFolders,
  listDocs,
  confirmUploadDocs,
  getUploadUrlDocs,
} from "../../services/api";


import {
  deleteDoc,
  deleteDocumentFolder,
  listDmsProjects,
  createDmsProject,
  deleteDmsProject,
} from "../../services/api";
const COMMON_DOC_TYPES = [
  "DIRECTORIO",
  "CONTRATO",
  "FACTURA",
  "COMPROBANTE",
  "INVENTARIO",
  "DOCUMENTO",
  "GENERAL",
];

const COMMON_STATUSES = [
  "CERT_READY",
  "TEXT_READY",
  "OCR_READY",
  "OCR_DONE",
  "PROCESSED",
  "UPLOADED",
  "INDEXED",
  "ERROR",
];

function normalize(v) {
  return (v ?? "").toString().trim();
}

function getDocId(doc) {
  return doc?.documentId || doc?.id || doc?.sk || doc?.s3Key || "";
}

function getDocFileName(doc) {
  return doc?.fileName || doc?.filename || doc?.name || "Documento sin nombre";
}

function getDocDate(doc) {
  return doc?.uploadedAt || doc?.createdAt || doc?.updatedAt || doc?.fechaCarga || "";
}

function getDocSize(doc) {
  return Number(doc?.sizeBytes || doc?.s3ContentLength || doc?.contentLength || 0);
}

function safeDate(iso) {
  if (!iso) return "-";

  try {
    return new Intl.DateTimeFormat("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "-";
  }
}

function bytesToSize(bytes = 0) {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = Number(bytes) || 0;
  let i = 0;

  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }

  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function getExt(fileName = "") {
  const parts = String(fileName).split(".");
  return parts.length > 1 ? parts.pop().toUpperCase() : "FILE";
}

function getTypeLabel(doc) {
  const mime = doc?.contentType || doc?.s3ContentType || "";
  const fileName = getDocFileName(doc);

  if (mime.includes("pdf") || fileName.toLowerCase().endsWith(".pdf")) return "PDF";
  if (mime.includes("spreadsheet") || /\.(xlsx|xls|csv)$/i.test(fileName)) return "Excel";
  if (mime.includes("word") || /\.(docx|doc)$/i.test(fileName)) return "Word";
  if (mime.startsWith("image/") || /\.(png|jpg|jpeg|webp)$/i.test(fileName)) return "Imagen";

  return getExt(fileName);
}

function getFileTone(type) {
  const t = normalize(type).toUpperCase();

  if (t === "PDF") {
    return {
      icon: "PDF",
      box: "bg-red-50 text-red-700 border-red-100",
      pill: "bg-red-50 text-red-700",
    };
  }

  if (t === "EXCEL") {
    return {
      icon: "XLS",
      box: "bg-emerald-50 text-emerald-700 border-emerald-100",
      pill: "bg-emerald-50 text-emerald-700",
    };
  }

  if (t === "WORD") {
    return {
      icon: "DOC",
      box: "bg-blue-50 text-blue-700 border-blue-100",
      pill: "bg-blue-50 text-blue-700",
    };
  }

  if (t === "IMAGEN") {
    return {
      icon: "IMG",
      box: "bg-purple-50 text-purple-700 border-purple-100",
      pill: "bg-purple-50 text-purple-700",
    };
  }

  return {
    icon: t.slice(0, 3) || "DOC",
    box: "bg-slate-50 text-slate-700 border-slate-200",
    pill: "bg-slate-100 text-slate-700",
  };
}

function statusMeta(status) {
  const s = normalize(status || "SIN_ESTATUS").toUpperCase();

  const map = {
    CERT_READY: {
      label: "Certificado",
      className: "bg-emerald-50 text-emerald-700 border-emerald-100",
      dot: "bg-emerald-500",
    },
    PROCESSED: {
      label: "Procesado",
      className: "bg-purple-50 text-purple-700 border-purple-100",
      dot: "bg-purple-500",
    },
    TEXT_READY: {
      label: "Texto listo",
      className: "bg-blue-50 text-blue-700 border-blue-100",
      dot: "bg-blue-500",
    },
    OCR_READY: {
      label: "OCR listo",
      className: "bg-blue-50 text-blue-700 border-blue-100",
      dot: "bg-blue-500",
    },
    OCR_DONE: {
      label: "OCR terminado",
      className: "bg-blue-50 text-blue-700 border-blue-100",
      dot: "bg-blue-500",
    },
    UPLOADED: {
      label: "Cargado",
      className: "bg-slate-50 text-slate-700 border-slate-200",
      dot: "bg-slate-400",
    },
    INDEXED: {
      label: "Indexado",
      className: "bg-slate-50 text-slate-700 border-slate-200",
      dot: "bg-slate-400",
    },
    ERROR: {
      label: "Error",
      className: "bg-red-50 text-red-700 border-red-100",
      dot: "bg-red-500",
    },
  };

  return (
    map[s] || {
      label: s,
      className: "bg-slate-50 text-slate-700 border-slate-200",
      dot: "bg-slate-400",
    }
  );
}

function extractItems(res) {
  const items = res?.items || res?.data?.items || res?.data || [];
  return Array.isArray(items) ? items : [];
}

function uniqueSorted(values) {
  return Array.from(new Set(values.map(normalize).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, "es")
  );
}

export default function ExploradorDocumental() {
  const [docs, setDocs] = useState([]);
  const [dmsProjects, setDmsProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState("");
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [project, setProject] = useState("TODOS");
  const [docType, setDocType] = useState("TODOS");
  const [status, setStatus] = useState("TODOS");
  const [extension, setExtension] = useState("TODOS");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [viewMode, setViewMode] = useState("drive");
  const [driveProject, setDriveProject] = useState("");
  const [driveDocType, setDriveDocType] = useState("");
  const [sortMode, setSortMode] = useState("newest");

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const projectsResponse = await listDmsProjects();

      const projectItems = Array.isArray(
        projectsResponse?.data?.items
      )
        ? projectsResponse.data.items
        : [];

      const activeProjects = projectItems.filter(
        (projectItem) =>
          projectItem?.projectName &&
          projectItem?.status !== "DELETED"
      );

      setDmsProjects(activeProjects);

      const responses = await Promise.allSettled(
        activeProjects.map((projectItem) =>
          listDocs({
            limit: 200,
            projectName: projectItem.projectName,
          })
        )
      );

      const documentos = responses.flatMap((r) =>
        r.status === "fulfilled" ? extractItems(r.value) : []
      );

      const dedup = Array.from(
        new Map(
          documentos.map((d) => [
            getDocId(d) || d.s3Key || d.fileName || d.filename || d.name,
            d,
          ])
        ).values()
      );

      setDocs(dedup);
    } catch (err) {
      console.error("ERROR EXPLORADOR DOCUMENTAL:", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
        url: err?.config?.url,
        params: err?.config?.params,
      });

      setDocs([]);
      setError(
        err?.response?.data?.message ||
          "No fue posible consultar el repositorio documental."
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    cargar();
  }, [cargar]);

  const projects = useMemo(() => {
    return [
      "TODOS",
      ...uniqueSorted([
        ...dmsProjects.map(
          (projectItem) => projectItem.projectName
        ),
        ...docs.map((d) => d.projectName),
      ]),
    ];
  }, [dmsProjects, docs]);

  const docTypes = useMemo(() => {
    return [
      "TODOS",
      ...uniqueSorted([
        ...COMMON_DOC_TYPES,
        ...docs.map((d) => d.docType),
      ]),
    ];
  }, [docs]);

  const statuses = useMemo(() => {
    return [
      "TODOS",
      ...uniqueSorted([
        ...COMMON_STATUSES,
        ...docs.map((d) => d.status),
      ]),
    ];
  }, [docs]);

  const extensions = useMemo(() => {
    return [
      "TODOS",
      ...uniqueSorted(docs.map((d) => getTypeLabel(d))),
    ];
  }, [docs]);

  const filtered = useMemo(() => {
    const q = normalize(query).toLowerCase();

    const result = docs.filter((d) => {
      if (project !== "TODOS" && normalize(d.projectName) !== project) return false;
      if (docType !== "TODOS" && normalize(d.docType) !== docType) return false;
      if (status !== "TODOS" && normalize(d.status) !== status) return false;
      if (extension !== "TODOS" && getTypeLabel(d) !== extension) return false;

      const docDate = getDocDate(d);

      if (dateFrom) {
        const docTime = docDate ? new Date(docDate).getTime() : 0;
        const fromTime = new Date(`${dateFrom}T00:00:00`).getTime();

        if (docTime < fromTime) return false;
      }

      if (dateTo) {
        const docTime = docDate ? new Date(docDate).getTime() : 0;
        const toTime = new Date(`${dateTo}T23:59:59`).getTime();

        if (docTime > toTime) return false;
      }

      if (!q) return true;

      const haystack = [
        getDocFileName(d),
        d.projectName,
        d.docType,
        d.status,
        getDocId(d),
        d.uploadedBy,
        d.municipio,
        d.oficina,
        d.tags,
        d.contentType,
        d.s3ContentType,
      ]
        .map((x) => normalize(Array.isArray(x) ? x.join(" ") : x).toLowerCase())
        .join(" ");

      return haystack.includes(q);
    });

    return result.sort((a, b) => {
      if (sortMode === "name") {
        return getDocFileName(a).localeCompare(getDocFileName(b), "es");
      }

      if (sortMode === "size") {
        return getDocSize(b) - getDocSize(a);
      }

      const ad = getDocDate(a) ? new Date(getDocDate(a)).getTime() : 0;
      const bd = getDocDate(b) ? new Date(getDocDate(b)).getTime() : 0;

      if (sortMode === "oldest") {
        return ad - bd;
      }

      return bd - ad;
    });
  }, [
    docs,
    query,
    project,
    docType,
    status,
    extension,
    dateFrom,
    dateTo,
    sortMode,
  ]);

  const resumen = useMemo(() => {
    const totalSize = filtered.reduce((acc, d) => acc + getDocSize(d), 0);

    const pdfs = filtered.filter((d) => getTypeLabel(d) === "PDF").length;
    const imagenes = filtered.filter((d) => getTypeLabel(d) === "Imagen").length;
    const office = filtered.filter((d) =>
      ["Excel", "Word"].includes(getTypeLabel(d))
    ).length;

    return {
      total: filtered.length,
      proyectos: new Set(
        filtered.map((d) => normalize(d.projectName)).filter(Boolean)
      ).size,
      pdfs,
      office,
      imagenes,
      totalSize,
    };
  }, [filtered]);

  const activeFilters = useMemo(() => {
    const items = [];

    if (query) {
      items.push({
        key: "query",
        label: `Búsqueda: ${query}`,
        onRemove: () => setQuery(""),
      });
    }

    if (project !== "TODOS") {
      items.push({
        key: "project",
        label: `Proyecto: ${project}`,
        onRemove: () => setProject("TODOS"),
      });
    }

    if (docType !== "TODOS") {
      items.push({
        key: "docType",
        label: `Tipo: ${docType}`,
        onRemove: () => setDocType("TODOS"),
      });
    }

    if (extension !== "TODOS") {
      items.push({
        key: "extension",
        label: `Extensión: ${extension}`,
        onRemove: () => setExtension("TODOS"),
      });
    }

    if (status !== "TODOS") {
      items.push({
        key: "status",
        label: `Estatus: ${status}`,
        onRemove: () => setStatus("TODOS"),
      });
    }

    if (dateFrom) {
      items.push({
        key: "dateFrom",
        label: `Desde: ${dateFrom}`,
        onRemove: () => setDateFrom(""),
      });
    }

    if (dateTo) {
      items.push({
        key: "dateTo",
        label: `Hasta: ${dateTo}`,
        onRemove: () => setDateTo(""),
      });
    }

    return items;
  }, [query, project, docType, extension, status, dateFrom, dateTo]);

  const limpiar = () => {
    setQuery("");
    setProject("TODOS");
    setDocType("TODOS");
    setStatus("TODOS");
    setExtension("TODOS");
    setDateFrom("");
    setDateTo("");
    setSortMode("newest");
  };

  const descargar = async (doc) => {
    try {
      const id = getDocId(doc);

      if (!id) {
        setError("Este documento no tiene identificador documental.");
        return;
      }

      setDownloadingId(id);
      setError("");

      const res = await getDownloadUrlDoc(id, "cert");
      const url = res?.url || res?.data?.url;

      if (!url) {
        setError("No llegó URL de descarga para este documento.");
        return;
      }

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("ERROR DESCARGA:", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });

      setError("No se pudo abrir o descargar el documento.");
    } finally {
      setDownloadingId("");
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fb] px-3 py-5 text-slate-950">
      <div className="w-full max-w-none">
        

        <section className={`mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 ${viewMode === "drive" ? "hidden" : ""}`}>
          <KpiCard title="Documentos" value={resumen.total} label="Filtrados" icon="DOC" />
          <KpiCard title="Proyectos" value={resumen.proyectos} label="Con documentos" icon="PRY" />
          <KpiCard title="PDF" value={resumen.pdfs} label="Documentos PDF" icon="PDF" />
          <KpiCard title="Office" value={resumen.office} label="Excel / Word" icon="OFF" />
          <KpiCard title="Imágenes" value={resumen.imagenes} label="Archivos visuales" icon="IMG" />
          <KpiCard title="Tamaño" value={bytesToSize(resumen.totalSize)} label="Estimado" icon="GB" />
        </section>

        <section className={`mb-5 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm md:p-5 ${viewMode === "drive" ? "hidden" : ""}`}>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.4fr_0.8fr_0.8fr_0.8fr]">
            <div className="relative">
              <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                ⌕
              </div>

              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre, proyecto, tipo, ID, usuario, oficina..."
                className="h-14 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
              />
            </div>

            <SelectBox value={project} onChange={setProject} options={projects} allLabel="Proyecto base / visibles" />
            <SelectBox value={docType} onChange={setDocType} options={docTypes} allLabel="Todos los tipos" />
            <SelectBox value={extension} onChange={setExtension} options={extensions} allLabel="Todas las extensiones" />
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <SelectBox value={status} onChange={setStatus} options={statuses} allLabel="Todos los estatus" />

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-900 outline-none transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
            />

            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-900 outline-none transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
            />

            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-900 outline-none transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
            >
              <option value="newest">Más recientes primero</option>
              <option value="oldest">Más antiguos primero</option>
              <option value="name">Nombre A-Z</option>
              <option value="size">Mayor tamaño</option>
            </select>

            <button
              type="button"
              onClick={limpiar}
              className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-900 transition hover:bg-slate-50"
            >
              Limpiar filtros
            </button>
          </div>

          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {activeFilters.length === 0 ? (
                <span className="rounded-full bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-500">
                  
                </span>
              ) : (
                activeFilters.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={item.onRemove}
                    className="rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs font-black text-indigo-700 transition hover:bg-indigo-100"
                  >
                    {item.label} ×
                  </button>
                ))
              )}
            </div>

            <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1">
              
              <button
                type="button"
                onClick={() => setViewMode("drive")}
                className={`rounded-xl px-4 py-2 text-xs font-black transition ${
                  viewMode === "drive"
                    ? "bg-slate-950 text-white shadow-sm"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                Drive Pro
              </button>
<button
                type="button"
                onClick={() => setViewMode("list")}
                className={`rounded-xl px-4 py-2 text-xs font-black transition ${
                  viewMode === "list"
                    ? "bg-slate-950 text-white shadow-sm"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                Lista
              </button>

              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`rounded-xl px-4 py-2 text-xs font-black transition ${
                  viewMode === "grid"
                    ? "bg-slate-950 text-white shadow-sm"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                Tarjetas
              </button>
            </div>
          </div>
        </section>

        {error && (
          <div className="mb-5 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-black text-slate-950">
                Documentos encontrados
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                {filtered.length} documento{filtered.length === 1 ? "" : "s"} disponible
                {filtered.length === 1 ? "" : "s"} en la vista actual.
              </p>
            </div>

           
          </div>

          {loading ? (
            <LoadingState />
          ) : viewMode === "drive" ? (

            <DriveProView
              docs={filtered}
              dmsProjects={dmsProjects}
              driveProject={driveProject}
              driveDocType={driveDocType}
              setDriveProject={setDriveProject}
              setDriveDocType={setDriveDocType}
              downloadingId={downloadingId}
              onDownload={descargar}
                          onRefresh={cargar}
            />
          ) : filtered.length === 0 ? (
            <EmptyState />
          ) : viewMode === "list" ? (
            <ListView
              docs={filtered}
              downloadingId={downloadingId}
              onDownload={descargar}
            />
          ) : (
            <GridView
              docs={filtered}
              downloadingId={downloadingId}
              onDownload={descargar}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function InfoPill({ label, value }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 shadow-sm">
      <span className="text-slate-400">{label}:</span>
      <span className="text-slate-900">{value}</span>
    </span>
  );
}

function KpiCard({ title, value, label, icon }) {
  return (
    <div className="group rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
            {title}
          </div>

          <div className="mt-2 text-2xl font-black tracking-tight text-slate-950">
            {value}
          </div>

          <div className="mt-1 text-xs font-medium text-slate-500">
            {label}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black text-slate-500 transition group-hover:bg-slate-950 group-hover:text-white">
          {icon}
        </div>
      </div>
    </div>
  );
}

function SelectBox({ value, onChange, options, allLabel }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-14 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-900 outline-none transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option === "TODOS" ? allLabel : option}
        </option>
      ))}
    </select>
  );
}

function FileTypeBadge({ doc }) {
  const type = getTypeLabel(doc);
  const tone = getFileTone(type);

  return (
    <span className={`inline-flex items-center rounded-xl px-3 py-1 text-xs font-black ${tone.pill}`}>
      {type}
    </span>
  );
}

function FileIcon({ doc }) {
  const type = getTypeLabel(doc);
  const tone = getFileTone(type);

  return (
    <div
      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border text-xs font-black ${tone.box}`}
    >
      {tone.icon}
    </div>
  );
}

function StatusPill({ status }) {
  const meta = statusMeta(status);

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-black ${meta.className}`}
    >
      <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

function groupDocumentsBy(docs, field) {
  const map = new Map();

  docs.forEach((doc) => {
    const key = normalize(doc?.[field]) || "SIN_CLASIFICAR";
    const current = map.get(key) || {
      name: key,
      count: 0,
      size: 0,
      latest: "",
      pdfs: 0,
      docs: [],
    };

    current.count += 1;
    current.size += getDocSize(doc);
    current.docs.push(doc);

    const docDate = getDocDate(doc);
    if (docDate && (!current.latest || new Date(docDate) > new Date(current.latest))) {
      current.latest = docDate;
    }

    if (getTypeLabel(doc) === "PDF") {
      current.pdfs += 1;
    }

    map.set(key, current);
  });

  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "es"));
}

function DriveProView({
  docs,
  dmsProjects,
  driveProject,
  driveDocType,
  setDriveProject,
  setDriveDocType,
  downloadingId,
  onDownload,
  onRefresh,
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [realFolders, setRealFolders] = useState([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [folderError, setFolderError] = useState("");
  const [folderStack, setFolderStack] = useState([]);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadingBatch, setUploadingBatch] = useState(false);
  const [draggingFiles, setDraggingFiles] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadXhrsRef = useRef(new Map());
  const multipartUploadsRef = useRef(new Map());

  const currentFolder =
    folderStack.length > 0 ? folderStack[folderStack.length - 1] : null;

  const currentFolderId = currentFolder?.folderId || "ROOT";

  const projectFolders = useMemo(() => {
    const documentGroups = new Map(
      groupDocumentsBy(docs, "projectName").map(
        (group) => [
          normalize(group.name),
          group,
        ]
      )
    );

    return dmsProjects.map((projectItem) => {
      const projectName = normalize(
        projectItem.projectName
      );

      const documentGroup =
        documentGroups.get(projectName);

      return {
        name: projectName,
        displayName:
          projectItem.displayName ||
          projectItem.projectName,
        count: documentGroup?.count || 0,
        size: documentGroup?.size || 0,
        latest:
          documentGroup?.latest ||
          projectItem.updatedAt ||
          projectItem.createdAt ||
          "",
        pdfs: documentGroup?.pdfs || 0,
        project: projectItem,
      };
    });
  }, [dmsProjects, docs]);

  const loadRealFolders = useCallback(async () => {
    if (!driveProject) {
      setRealFolders([]);
      setFolderError("");
      return;
    }

    try {
      setFolderLoading(true);
      setFolderError("");

      const response = await listDocumentFolders({
        projectName: driveProject,
        parentFolderId: currentFolderId,
        limit: 100,
      });

      const items = response?.items || response?.data?.items || [];
      setRealFolders(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error("ERROR LISTANDO CARPETAS:", {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      setRealFolders([]);
      setFolderError(
        error?.response?.data?.message ||
          "No fue posible consultar las carpetas."
      );
    } finally {
      setFolderLoading(false);
    }
  }, [driveProject, currentFolderId]);

  useEffect(() => {
    loadRealFolders();
  }, [loadRealFolders]);

  useEffect(() => {
    setFolderStack([]);
    setRealFolders([]);
    setSelectedItem(null);
  }, [driveProject]);

  const currentScopeDocs = useMemo(() => {
    if (!driveProject) return docs;

    const projectDocs = docs.filter(
      (doc) => normalize(doc.projectName) === driveProject
    );

    const folderDocs = projectDocs.filter(
      (doc) => normalize(doc.folderId || "ROOT") === currentFolderId
    );

    return folderDocs;
  }, [docs, driveProject, driveDocType, currentFolderId]);


  const rows = useMemo(() => {
    if (!driveProject) {
      return projectFolders.map((folder) => ({
        kind: "project",
        key: `project-${folder.name}`,
        title:
          folder.displayName ||
          folder.name,
        subtitle: "Proyecto documental",
        count: folder.count,
        size: folder.size,
        latest: folder.latest,
        pdfs: folder.pdfs,
        raw: folder,
      }));
    }

    const folderRows = realFolders.map((folder) => ({
      kind: "real-folder",
      key: `folder-${folder.folderId}`,
      title: folder.name,
      subtitle:
        folder.parentFolderId === "ROOT" ? driveProject : "Subcarpeta",
      count: folder.itemCount || 0,
      size: folder.sizeBytes || 0,
      latest: folder.updatedAt || folder.createdAt || "",
      pdfs: 0,
      folder,
      raw: folder,
    }));

    const documentRows = currentScopeDocs.map((doc, index) => ({
      kind: "document",
      key: getDocId(doc) || `${getDocFileName(doc)}-${index}`,
      title: getDocFileName(doc),
      subtitle: getDocId(doc),
      doc,
      raw: doc,
    }));

    if (
      currentFolderId === "ROOT" &&
      driveDocType
    ) {
      return documentRows;
    }

    return [...folderRows, ...documentRows];
  }, [
    driveProject,
    driveDocType,
    projectFolders,

    currentScopeDocs,
    realFolders,
    currentFolderId,
  ]);

  const totalSize = currentScopeDocs.reduce(
    (acc, doc) => acc + getDocSize(doc),
    0
  );

  const pdfCount = currentScopeDocs.filter(
    (doc) => getTypeLabel(doc) === "PDF"
  ).length;

  const currentTitle = !driveProject
    ? "Home documental"
    : currentFolder
      ? currentFolder.name
      : driveDocType || driveProject;

  const openRow = (row) => {
    setSelectedItem(row);

    if (row.kind === "project") {
      const projectName =
        row.raw?.name ||
        row.raw?.project?.projectName ||
        row.projectName ||
        row.title;

      setDriveProject(projectName);
      setDriveDocType("");
      setFolderStack([]);
      setSelectedItem(null);
      return;
    }

    if (row.kind === "real-folder") {
      setDriveDocType("");
      setFolderStack((previous) => [
        ...previous,
        {
          folderId: row.folder.folderId,
          name: row.folder.name,
        },
      ]);
      setSelectedItem(null);
      return;
    }

    if (row.kind === "type") {
      setDriveDocType("");
    }
  };

  const goToFolderLevel = (index) => {
    if (index < 0) {
      setFolderStack([]);
      setDriveDocType("");
      setSelectedItem(null);
      return;
    }

    setFolderStack((previous) => previous.slice(0, index + 1));
    setDriveDocType("");
    setSelectedItem(null);
  };

  const createFolder = async () => {
    const name = String(
      newFolderName || ""
    ).trim();

    if (name.length < 2) {
      setFolderError(
        driveProject
          ? "Escribe un nombre de carpeta válido."
          : "Escribe un nombre de proyecto válido."
      );
      return;
    }

    try {
      setCreatingFolder(true);
      setFolderError("");

      if (!driveProject) {
        await createDmsProject({
          displayName: name,
        });

        setNewFolderName("");
        setShowCreateFolder(false);
        setSelectedItem(null);

        await Promise.resolve(
          onRefresh?.()
        );

        return;
      }

      await createDocumentFolder({
        projectName: driveProject,
        parentFolderId: currentFolderId,
        name,
      });

      setNewFolderName("");
      setShowCreateFolder(false);
      setSelectedItem(null);

      await Promise.resolve(
        onRefresh?.()
      );

      await loadRealFolders();
    } catch (error) {
      setFolderError(
        error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          (driveProject
            ? "No fue posible crear la carpeta."
            : "No fue posible crear el proyecto.")
      );
    } finally {
      setCreatingFolder(false);
    }
  };

  const deleteSelectedItem = useCallback(async () => {
    const item = selectedItem;

    if (!item) {
      setFolderError(
        "Selecciona un proyecto, una carpeta o un documento."
      );
      return;
    }

    const isProject =
      item.kind === "project";

    const isDocument =
      item.kind === "document";

    const isRealFolder =
      item.kind === "real-folder";

    if (
      !isProject &&
      !isDocument &&
      !isRealFolder
    ) {
      setFolderError(
        "El elemento seleccionado no puede eliminarse."
      );
      return;
    }

    const label =
      item.title ||
      item.doc?.documentName ||
      item.doc?.fileName ||
      item.folder?.name ||
      "elemento seleccionado";

    const confirmed = window.confirm(
      isProject
        ? `¿Eliminar el proyecto "${label}"? Solo se eliminará si está vacío.`
        : `¿Eliminar "${label}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingItem(true);
      setFolderError("");

      if (isProject) {
        const projectName =
          item.raw?.name ||
          item.raw?.project?.projectName ||
          item.projectName;

        if (!projectName) {
          throw new Error(
            "El proyecto no tiene projectName."
          );
        }

        await deleteDmsProject(
          projectName
        );

        setDriveProject("");
        setDriveDocType("");
        setFolderStack([]);
        setSelectedItem(null);

        await Promise.resolve(
          onRefresh?.()
        );

        return;
      }

      if (isDocument) {
        const documentId =
          getDocId(item.doc || item.raw);

        if (!documentId) {
          throw new Error(
            "El documento no tiene documentId."
          );
        }

        await deleteDoc(
          documentId
        );
      } else {
        const folderId =
          item.folder?.folderId ||
          item.raw?.folderId ||
          item.folderId;

        if (!folderId) {
          throw new Error(
            "La carpeta no tiene folderId."
          );
        }

        await deleteDocumentFolder(
          folderId,
          driveProject
        );
      }

      setSelectedItem(null);

      await Promise.resolve(
        onRefresh?.()
      );

      await loadRealFolders();
    } catch (error) {
      setFolderError(
        error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          "No fue posible eliminar el elemento."
      );
    } finally {
      setDeletingItem(false);
    }
  }, [
    driveProject,
    loadRealFolders,
    onRefresh,
    selectedItem,
    setDriveDocType,
    setDriveProject,
  ]);

  const updateUploadItem = useCallback((id, patch) => {
    setUploadQueue((previous) =>
      previous.map((item) =>
        item.id === id ? { ...item, ...patch } : item
      )
    );
  }, []);

  const uploadToSignedUrl = useCallback(
    ({ id, uploadUrl, file, contentType }) =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        uploadXhrsRef.current.set(id, xhr);

        xhr.open("PUT", uploadUrl, true);
        xhr.setRequestHeader(
          "Content-Type",
          contentType || "application/octet-stream"
        );

        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;

          const progress = Math.max(
            1,
            Math.min(99, Math.round((event.loaded / event.total) * 100))
          );

          updateUploadItem(id, {
            status: "UPLOADING",
            progress,
          });
        };

        xhr.onload = () => {
          uploadXhrsRef.current.delete(id);

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
            return;
          }

          reject(new Error(`S3 respondió ${xhr.status || "sin estatus"}`));
        };

        xhr.onerror = () => {
          uploadXhrsRef.current.delete(id);
          reject(new Error("Falló la conexión durante la carga."));
        };

        xhr.onabort = () => {
          uploadXhrsRef.current.delete(id);
          reject(new Error("Carga cancelada."));
        };

        xhr.send(file);
      }),
    [updateUploadItem]
  );

  const requestMultipart = useCallback(async (path, payload) => {
    const base = process.env.REACT_APP_API_DMS_MULTIPART || "https://nu032c3243.execute-api.us-east-1.amazonaws.com/dev";
    const response = await fetch(`${base}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || `Error multipart HTTP ${response.status}`);
    return data;
  }, []);

  const uploadMultipartPart = useCallback(
    ({ id, uploadUrl, blob, partNumber, onProgress }) =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        uploadXhrsRef.current.set(id, xhr);
        xhr.open("PUT", uploadUrl, true);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) onProgress?.(event.loaded, event.total);
        };
        xhr.onload = () => {
          if (xhr.status < 200 || xhr.status >= 300) {
            reject(new Error(`S3 rechazó la parte ${partNumber} con HTTP ${xhr.status}`));
            return;
          }
          const eTag = xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag");
          if (!eTag) {
            reject(new Error("S3 no expuso el ETag. Revisa CORS del bucket."));
            return;
          }
          resolve({ partNumber, eTag });
        };
        xhr.onerror = () => reject(new Error(`Error de red en la parte ${partNumber}`));
        xhr.onabort = () => reject(new Error("Carga cancelada por el usuario."));
        xhr.send(blob);
      }),
    []
  );

  const uploadMultipartFile = useCallback(
    async ({ id, file, contentType }) => {
      const init = await requestMultipart("/dms/multipart/initiate", {
        fileName: file.name,
        sizeBytes: file.size,
        contentType,
        projectName: driveProject,
        docType: "GENERAL",
        folderId: currentFolderId,
      });
      if (!init.uploadId || !init.s3Key) throw new Error("El backend multipart no devolvió uploadId o s3Key.");
      multipartUploadsRef.current.set(id, { uploadId: init.uploadId, s3Key: init.s3Key });
      const partSize = 64 * 1024 * 1024;
      const partCount = Math.ceil(file.size / partSize);
      const loadedByPart = new Map();
      const completedParts = [];
      let nextPart = 1;
      let cancelled = false;
      const updateTotalProgress = () => {
        let loaded = 0;
        loadedByPart.forEach((value) => { loaded += value; });
        const percent = Math.min(98, Math.max(1, Math.round((loaded / file.size) * 98)));
        updateUploadItem(id, { status: "UPLOADING", progress: percent });
      };
      const uploadWithRetry = async (partNumber) => {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const blob = file.slice(start, end);
        let lastError = null;
        for (let attempt = 1; attempt <= 3; attempt += 1) {
          if (cancelled) throw new Error("Carga cancelada por el usuario.");
          try {
            const signed = await requestMultipart("/dms/multipart/sign-part", {
              uploadId: init.uploadId,
              s3Key: init.s3Key,
              partNumber,
            });
            const result = await uploadMultipartPart({
              id,
              uploadUrl: signed.uploadUrl,
              blob,
              partNumber,
              onProgress: (loaded) => {
                loadedByPart.set(partNumber, loaded);
                updateTotalProgress();
              },
            });
            loadedByPart.set(partNumber, blob.size);
            updateTotalProgress();
            return result;
          } catch (error) {
            lastError = error;
            if (String(error?.message || "").toLowerCase().includes("cancelada")) {
              cancelled = true;
              throw error;
            }
            if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, attempt * 800));
          }
        }
        throw lastError || new Error(`No se pudo subir la parte ${partNumber}`);
      };
      const worker = async () => {
        while (true) {
          const partNumber = nextPart;
          nextPart += 1;
          if (partNumber > partCount) return;
          completedParts.push(await uploadWithRetry(partNumber));
        }
      };
      try {
        await Promise.all([worker(), worker()]);
        completedParts.sort((a, b) => a.partNumber - b.partNumber);
        updateUploadItem(id, { status: "CONFIRMING", progress: 99 });
        await requestMultipart("/dms/multipart/complete", {
          uploadId: init.uploadId,
          s3Key: init.s3Key,
          parts: completedParts,
        });
        multipartUploadsRef.current.delete(id);
        uploadXhrsRef.current.delete(id);
        return { ...init, uploadMode: "MULTIPART" };
      } catch (error) {
        const active = multipartUploadsRef.current.get(id);
        if (active) {
          try { await requestMultipart("/dms/multipart/abort", active); }
          catch (abortError) { console.error("No fue posible abortar multipart:", abortError); }
        }
        multipartUploadsRef.current.delete(id);
        uploadXhrsRef.current.delete(id);
        throw error;
      }
    },
    [currentFolderId, driveProject, requestMultipart, updateUploadItem, uploadMultipartPart]
  );

  const uploadOneFile = useCallback(
    async (entry) => {
      const file = entry.file;
      const contentType = file.type || "application/octet-stream";
      const multipartThreshold = 100 * 1024 * 1024;
      updateUploadItem(entry.id, { status: "PREPARING", progress: 1, error: "" });
      try {
        let uploadData;
        if (file.size > multipartThreshold) {
          uploadData = await uploadMultipartFile({ id: entry.id, file, contentType });
        } else {
          const uploadResponse = await getUploadUrlDocs({
            fileName: file.name,
            contentType,
            size: file.size,
            projectName: driveProject,
            expedienteId: driveProject,
            docType: "GENERAL",
            tipo: "GENERAL",
            folderId: currentFolderId,
            relativePath: file.webkitRelativePath || file._relativePath || file.name,
            certificationRequired: false,
            processingMode: "NONE",
          });
          uploadData = uploadResponse?.data || uploadResponse || {};
          if (!uploadData.uploadUrl || !uploadData.documentId) throw new Error("El backend no devolvió URL o documentId.");
          await uploadToSignedUrl({
            id: entry.id,
            uploadUrl: uploadData.uploadUrl,
            file,
            contentType: uploadData.contentType || contentType,
          });
        }
        if (!uploadData.documentId || !(uploadData.s3Key || uploadData.key)) throw new Error("La carga no devolvió documentId o s3Key.");
        updateUploadItem(entry.id, { status: "CONFIRMING", progress: 99 });
        const baseName = String(file.name || "Archivo").replace(/.[^.]+$/, "").trim() || "Archivo";
        await confirmUploadDocs({
          documentId: uploadData.documentId,
          projectName: uploadData.projectName || driveProject,
          docType: uploadData.docType || "GENERAL",
          folderId: uploadData.folderId || currentFolderId,
          fileName: uploadData.fileName || file.name,
          contentType: uploadData.contentType || contentType,
          s3Key: uploadData.s3Key || uploadData.key,
          sizeBytes: file.size,
          documentName: baseName.length >= 3 ? baseName : `Archivo ${baseName}`,
          normalizedName: baseName.length >= 3 ? baseName : `Archivo ${baseName}`,
          relativePath: file.webkitRelativePath || file._relativePath || file.name,
          batchName: currentFolder?.name || driveProject,
          batchKey: currentFolderId,
          certificationRequired: false,
          processingMode: "NONE",
          uploadMode: uploadData.uploadMode || (file.size > multipartThreshold ? "MULTIPART" : "SIMPLE"),
        });
        updateUploadItem(entry.id, { status: "DONE", progress: 100, error: "" });
      } catch (error) {
        const cancelled = String(error?.message || "").toLowerCase().includes("cancelada");
        updateUploadItem(entry.id, {
          status: cancelled ? "CANCELLED" : "ERROR",
          error: error?.response?.data?.message || error?.message || "No fue posible subir el archivo.",
        });
      } finally {
        uploadXhrsRef.current.delete(entry.id);
      }
    },
    [currentFolder, currentFolderId, driveProject, updateUploadItem, uploadMultipartFile, uploadToSignedUrl]
  );

  const startUploadBatch = useCallback(
    async (files) => {
      if (!driveProject || driveDocType) {
        setFolderError(
          "Entra al proyecto o a una carpeta real antes de subir archivos."
        );
        return;
      }

      const normalizedFiles = Array.from(files || []).filter(
        (file) => file && typeof file.size === "number"
      );

      if (normalizedFiles.length === 0) return;

      const entries = normalizedFiles.map((file, index) => ({
        id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
        file,
        name: file.name,
        size: file.size,
        status: "PENDING",
        progress: 0,
        error: "",
      }));

      setFolderError("");
      setUploadQueue(entries);
      setUploadingBatch(true);

      let cursor = 0;
      const concurrency = Math.min(3, entries.length);

      const worker = async () => {
        while (cursor < entries.length) {
          const currentIndex = cursor;
          cursor += 1;
          await uploadOneFile(entries[currentIndex]);
        }
      };

      try {
        await Promise.all(
          Array.from({ length: concurrency }, () => worker())
        );

        await Promise.resolve(onRefresh?.());
        await loadRealFolders();
      } finally {
        setUploadingBatch(false);
      }
    },
    [
      driveProject,
      driveDocType,
      loadRealFolders,
      onRefresh,
      uploadOneFile,
    ]
  );

  const cancelUpload = useCallback(
    async (id) => {
      const xhr = uploadXhrsRef.current.get(id);
      if (xhr) xhr.abort();
      const multipart = multipartUploadsRef.current.get(id);
      if (multipart) {
        try { await requestMultipart("/dms/multipart/abort", multipart); }
        catch (error) { console.error("No fue posible cancelar multipart:", error); }
        finally { multipartUploadsRef.current.delete(id); }
      }
      updateUploadItem(id, { status: "CANCELLED", error: "Carga cancelada por el usuario." });
    },
    [requestMultipart, updateUploadItem]
  );

  const retryUpload = useCallback(
    async (item) => {
      updateUploadItem(item.id, {
        status: "PENDING",
        progress: 0,
        error: "",
      });

      setUploadingBatch(true);
      try {
        await uploadOneFile(item);
        await Promise.resolve(onRefresh?.());
      } finally {
        setUploadingBatch(false);
      }
    },
    [onRefresh, updateUploadItem, uploadOneFile]
  );

  const readEntry = useCallback(async (entry, parentPath = "") => {
    if (!entry) return [];

    if (entry.isFile) {
      return new Promise((resolve) => {
        entry.file((file) => {
          try {
            Object.defineProperty(file, "_relativePath", {
              value: `${parentPath}${file.name}`,
              configurable: true,
            });
          } catch (_) {
            file._relativePath = `${parentPath}${file.name}`;
          }
          resolve([file]);
        }, () => resolve([]));
      });
    }

    if (entry.isDirectory) {
      const reader = entry.createReader();
      const children = [];

      while (true) {
        const batch = await new Promise((resolve) =>
          reader.readEntries(resolve, () => resolve([]))
        );
        if (!batch.length) break;
        children.push(...batch);
      }

      const nested = await Promise.all(
        children.map((child) =>
          readEntry(child, `${parentPath}${entry.name}/`)
        )
      );

      return nested.flat();
    }

    return [];
  }, []);

  const handleDrop = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDraggingFiles(false);

      const items = Array.from(event.dataTransfer?.items || []);
      const entries = items
        .map((item) =>
          typeof item.webkitGetAsEntry === "function"
            ? item.webkitGetAsEntry()
            : null
        )
        .filter(Boolean);

      if (entries.length > 0) {
        const nested = await Promise.all(
          entries.map((entry) => readEntry(entry))
        );
        await startUploadBatch(nested.flat());
        return;
      }

      await startUploadBatch(event.dataTransfer?.files || []);
    },
    [readEntry, startUploadBatch]
  );

  const uploadSummary = useMemo(() => {
    const total = uploadQueue.length;
    const done = uploadQueue.filter((item) => item.status === "DONE").length;
    const errors = uploadQueue.filter((item) => item.status === "ERROR").length;
    const cancelled = uploadQueue.filter(
      (item) => item.status === "CANCELLED"
    ).length;

    const progress =
      total === 0
        ? 0
        : Math.round(
            uploadQueue.reduce(
              (sum, item) => sum + Number(item.progress || 0),
              0
            ) / total
          );

    return { total, done, errors, cancelled, progress };
  }, [uploadQueue]);

  const visibleRows = useMemo(
    () => rows,
    [rows]
  );

  const selectedForPanel =
    selectedItem || null;

  return (
    <div className="border-t border-[#d8dcef] bg-[#eceefa] text-[#15172f]">
      <div className="grid min-h-[640px] grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)_360px]">
        <aside className="bg-[#252a55] text-white">
          <div className="flex h-[72px] items-center gap-3 border-b border-white/10 px-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-white/10 text-lg">
              ◆
            </div>
            <div>
              <div className="text-xl font-black tracking-tight">IP Depot</div>
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/45">
                Document Management
              </div>
            </div>
          </div>

          <div className="p-4">
            <nav className="space-y-1">
              <button
                type="button"
                onClick={() => {
                  setDriveProject("");
                  setDriveDocType("");
                  setFolderStack([]);
                  setSelectedItem(null);
                }}
                className={`flex w-full items-center gap-3 px-5 py-3 text-left text-sm font-black transition ${
                  !driveProject
                    ? "bg-white/12 text-white"
                    : "text-white/45 hover:bg-white/8 hover:text-white"
                }`}
              >
                <span>⌂</span>
                Home
              </button>
            </nav>

            <div className="mt-8 border-t border-white/10 pt-5">
              <div className="mb-3 px-4 text-[10px] font-black uppercase tracking-[0.2em] text-white/35">
                Proyectos
              </div>

              <div className="space-y-1">
                {projectFolders.map((folder) => (
                  <button
                    key={folder.name}
                    type="button"
                    onClick={() => {
                      setDriveProject(folder.name);
                      setDriveDocType("");
                      setFolderStack([]);
                      setSelectedItem(null);
                    }}
                    className={`flex w-full items-start gap-3 px-5 py-3.5 text-left transition ${
                      driveProject === folder.name
                        ? "bg-white text-[#252a55]"
                        : "text-white/60 hover:bg-white/8 hover:text-white"
                    }`}
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-white/10 text-[10px] font-black">
                      P
                    </span>
                    <span className="min-w-0 flex-1 overflow-hidden">
                      <span className="block break-words text-xs font-black uppercase leading-4">
                        {folder.displayName || folder.name}
                      </span>
                      <span className="block text-[11px] opacity-60 leading-4">
                        {folder.count} documentos
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>


          </div>
        </aside>

        <main className="min-w-0 bg-[#e9ebf8]">
          <div className="flex min-h-[72px] items-center justify-between border-b border-[#d8dcef] bg-[#f0f1fb] px-4">
            <div className="relative w-full max-w-none">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e7391]">⌕</span>
              <input
                value=""
                readOnly
                placeholder="Buscar documentos, carpetas o identificadores..."
                className="h-9 w-full border border-[#cfd4ea] bg-[#e7e9f7] pl-9 pr-3 text-xs font-bold text-[#252a55] outline-none"
              />
            </div>

            <div className="ml-4 rounded-sm border border-[#cfd4ea] bg-[#f7f8ff] px-3 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#6e7391]">
              Drive documental
            </div>
          </div>

          <div className="border-b border-[#d8dcef] bg-[#f0f1fb] px-5 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-2xl font-black tracking-tight text-[#15172f]">
                  {currentTitle}
                </h2>

                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#6e7391]">
                  <button
                    type="button"
                    onClick={() => {
                      setDriveProject("");
                      setDriveDocType("");
                      setFolderStack([]);
                      setSelectedItem(null);
                    }}
                    className="hover:text-[#15172f]"
                  >
                    Root
                  </button>

                  {driveProject && (
                    <>
                      <span>›</span>
                      <button
                        type="button"
                        onClick={() => goToFolderLevel(-1)}
                        className="hover:text-[#15172f]"
                      >
                        {driveProject}
                      </button>
                    </>
                  )}

                  {folderStack.map((folder, index) => (
                    <React.Fragment key={folder.folderId}>
                      <span>›</span>
                      <button
                        type="button"
                        onClick={() => goToFolderLevel(index)}
                        className={
                          index === folderStack.length - 1
                            ? "text-[#15172f]"
                            : "hover:text-[#15172f]"
                        }
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}

                  {driveDocType && (
                    <>
                      <span>›</span>
                      <span className="text-[#15172f]">{driveDocType}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={loadRealFolders}
                  disabled={!driveProject || folderLoading}
                  className="border border-[#cfd4ea] bg-[#f7f8ff] px-4 py-2 text-xs font-black text-[#15172f] disabled:opacity-40"
                >
                  {folderLoading ? "Actualizando..." : "Actualizar"}
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(event) => {
                    startUploadBatch(event.target.files);
                    event.target.value = "";
                  }}
                />

                <input
                  ref={folderInputRef}
                  type="file"
                  multiple
                  webkitdirectory=""
                  directory=""
                  className="hidden"
                  onChange={(event) => {
                    startUploadBatch(event.target.files);
                    event.target.value = "";
                  }}
                />

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!driveProject || Boolean(driveDocType) || uploadingBatch}
                  className="border border-[#11132b] bg-white px-4 py-2 text-xs font-black text-[#11132b] disabled:opacity-40"
                >
                  + Subir archivos
                </button>

                <button
                  type="button"
                  onClick={() => folderInputRef.current?.click()}
                  disabled={!driveProject || Boolean(driveDocType) || uploadingBatch}
                  className="border border-[#11132b] bg-white px-4 py-2 text-xs font-black text-[#11132b] disabled:opacity-40"
                >
                  Subir carpeta
                </button>                <button
                  type="button"
                  onClick={deleteSelectedItem}
                  disabled={
                    !selectedItem ||
                    deletingItem ||
                    !["project", "document", "real-folder"].includes(selectedItem?.kind)
                  }
                  className="border border-red-300 bg-white px-4 py-2 text-xs font-black text-red-700 disabled:cursor-not-allowed disabled:opacity-35"
                >
                  {deletingItem ? "Eliminando..." : "Eliminar"}
                </button>


                <button
                  type="button"
                  onClick={() => {
                    setFolderError("");
                    setNewFolderName("");
                    setShowCreateFolder(true);
                  }}
                  disabled={Boolean(driveDocType)}
                  className="bg-[#11132b] px-5 py-2 text-xs font-black text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {driveProject
                    ? "+ Nueva carpeta"
                    : "+ Nuevo proyecto"}
                </button>
              </div>
            </div>

            {folderError && (
              <div className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-xs font-bold text-red-700">
                {folderError}
              </div>
            )}
          </div>

          {driveProject && !driveDocType && (
            <div className="px-5 pt-4">
              <div
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDraggingFiles(true);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "copy";
                  setDraggingFiles(true);
                }}
                onDragLeave={() => setDraggingFiles(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed px-5 py-6 text-center transition ${
                  draggingFiles
                    ? "border-[#4b67e9] bg-[#dfe4ff]"
                    : "border-[#bcc3df] bg-[#f4f5fc]"
                }`}
              >
                <div className="text-sm font-black text-[#15172f]">
                  Arrastra archivos, lotes o carpetas completas aquí­
                </div>
                <div className="mt-1 text-xs font-bold text-[#6e7391]">
                  Cualquier extension - carga multipart automatica - hasta 5 GB por archivo
                </div>
              </div>

              {uploadQueue.length > 0 && (
                <div className="mt-3 border border-[#d8dcef] bg-[#f7f8ff]">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#d8dcef] px-4 py-3">
                    <div className="text-xs font-black text-[#15172f]">
                      Cola: {uploadSummary.done}/{uploadSummary.total} completados
                      {uploadSummary.errors > 0
                        ? ` · ${uploadSummary.errors} errores`
                        : ""}
                    </div>
                    <div className="text-xs font-black text-[#15172f]">
                      {uploadSummary.progress}%
                    </div>
                  </div>

                  <div className="max-h-72 overflow-y-auto">
                    {uploadQueue.map((item) => (
                      <div
                        key={item.id}
                        className="grid grid-cols-[minmax(0,1fr)_80px_110px] items-center gap-3 border-b border-[#e1e4f1] px-4 py-3 last:border-b-0"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-xs font-black text-[#15172f]">
                            {item.name}
                          </div>
                          <div className="mt-1 text-[10px] font-bold text-[#6e7391]">
                            {bytesToSize(item.size)} · {item.status}
                          </div>
                          <div className="mt-2 h-1.5 bg-[#dfe3f2]">
                            <div
                              className="h-1.5 bg-[#4b67e9]"
                              style={{ width: `${item.progress || 0}%` }}
                            />
                          </div>
                          {item.error && (
                            <div className="mt-1 text-[10px] font-bold text-red-700">
                              {item.error}
                            </div>
                          )}
                        </div>

                        <div className="text-right text-xs font-black text-[#15172f]">
                          {item.progress || 0}%
                        </div>

                        <div className="flex justify-end gap-2">
                          {["PREPARING", "UPLOADING", "CONFIRMING"].includes(
                            item.status
                          ) && (
                            <button
                              type="button"
                              onClick={() => cancelUpload(item.id)}
                              className="border border-red-200 bg-white px-2 py-1 text-[10px] font-black text-red-700"
                            >
                              Cancelar
                            </button>
                          )}

                          {["ERROR", "CANCELLED"].includes(item.status) && (
                            <button
                              type="button"
                              onClick={() => retryUpload(item)}
                              disabled={uploadingBatch}
                              className="bg-[#11132b] px-2 py-1 text-[10px] font-black text-white disabled:opacity-40"
                            >
                              Reintentar
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <div className="min-w-[1320px]">
              <DmsLexisHeader
                isDocumentLevel={Boolean(
                  driveProject &&
                    (driveDocType || currentFolderId !== "ROOT")
                )}
              />

              {folderLoading && rows.length === 0 ? (
                <div className="px-5 py-14 text-center text-sm font-bold text-[#6e7391]">
                  Consultando carpetas...
                </div>
              ) : rows.length === 0 ? (
                <div className="px-5 py-14 text-center">
                  <div className="text-sm font-black text-[#15172f]">
                    Esta ubicación está vacía
                  </div>
                  <div className="mt-2 text-xs font-bold text-[#6e7391]">
                    Crea una carpeta para comenzar a organizar el repositorio.
                  </div>
                </div>
              ) : (
                visibleRows.map((row) =>
                  row.kind === "document" ? (
                    <DmsLexisDocumentRow
                      key={row.key}
                      row={row}
                      selected={selectedForPanel?.key === row.key}
                      onSelect={() => setSelectedItem(row)}
                      downloadingId={downloadingId}
                      onDownload={onDownload}
                    />
                  ) : (
                    <DmsLexisFolderRow
                      key={row.key}
                      row={row}
                      selected={selectedForPanel?.key === row.key}
                      onSelect={() => setSelectedItem(row)}
                      onOpen={() => openRow(row)}
                    />
                  )
                )
              )}
            </div>
          </div>

          <div className="flex h-[48px] items-center justify-between border-t border-[#d8dcef] bg-[#f0f1fb] px-4 text-[11px] font-black uppercase tracking-[0.12em] text-[#6e7391]">
            <div>Mostrando {visibleRows.length} elementos</div>
            <div>Carpeta actual: {currentFolderId}</div>
          </div>
        </main>

        <aside className="bg-[#f0f1fb]">
          <div className="flex h-[72px] items-center justify-between border-b border-[#d8dcef] px-4">
            <div className="flex items-center gap-2 text-sm font-black text-[#15172f]">
              <span className="text-[#4b67e9]">ⓘ</span>
              Detalle del resultado
            </div>
          </div>

          <DmsLexisProperties
            item={selectedForPanel}
            docs={currentScopeDocs}
            driveProject={driveProject}
            driveDocType={currentFolder?.name || driveDocType}
            totalSize={totalSize}
            pdfCount={pdfCount}
            onDownload={onDownload}
            downloadingId={downloadingId}
          />
        </aside>
      </div>

      {showCreateFolder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 p-4">
          <div className="w-full max-w-md border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-6 py-5">
              <div className="text-lg font-black text-slate-950">
                {driveProject
                  ? "Nueva carpeta"
                  : "Nuevo proyecto"}
              </div>
              <div className="mt-1 text-xs font-bold text-slate-500">
                {driveProject
                  ? `Ubicación: ${currentFolder?.name || driveProject}`
                  : "Se creará en Home documental"}
              </div>
            </div>

            <div className="px-6 py-5">
              <label className="block text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                Nombre
              </label>

              <input
                autoFocus
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !creatingFolder) {
                    createFolder();
                  }

                  if (event.key === "Escape") {
                    setShowCreateFolder(false);
                  }
                }}
                placeholder="Ej. Contratos 2026"
                className="mt-2 h-12 w-full border border-slate-300 px-4 text-sm font-bold text-slate-950 outline-none focus:border-indigo-500"
              />
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowCreateFolder(false)}
                disabled={creatingFolder}
                className="border border-slate-300 bg-white px-4 py-2 text-xs font-black text-slate-700 disabled:opacity-50"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={createFolder}
                disabled={
                  creatingFolder || normalize(newFolderName).length < 2
                }
                className="bg-[#11132b] px-5 py-2 text-xs font-black text-white disabled:opacity-40"
              >
                {creatingFolder
                  ? "Creando..."
                  : driveProject
                    ? "Crear carpeta"
                    : "Crear proyecto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DmsLexisHeader({ isDocumentLevel }) {
  const cols = isDocumentLevel
    ? ["", "Tipo", "Nombre e identificador", "Estatus", "Versión", "Propietario", "Modificado", "Acción"]
    : ["", "Tipo", "Nombre", "Estatus", "Elementos", "Peso", "Último mov.", "Acción"];

  return (
    <div className="grid grid-cols-[34px_50px_minmax(260px,1fr)_110px_90px_120px_130px_110px] border-b border-[#d8dcef] bg-[#e2e5f4] px-5 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-[#6e7391]">
      {cols.map((col, index) => (
        <div key={`${col}-${index}`}>{col}</div>
      ))}
    </div>
  );
}

function DmsLexisFolderRow({ row, selected, onSelect, onOpen }) {
  return (
    <div
      onClick={onSelect}
      onDoubleClick={onOpen}
      className={`grid cursor-pointer grid-cols-[34px_50px_minmax(260px,1fr)_110px_90px_120px_130px_110px] items-center border-b border-[#d8dcef] px-5 py-4 text-sm transition ${
        selected ? "bg-[#dfe4ff]" : "bg-[#eef0fb] hover:bg-[#e5e8f7]"
      }`}
    >
      <div>
        <input
          type="checkbox"
          checked={selected}
          onChange={(event) => {
            event.stopPropagation();
            onSelect();
          }}
          onClick={(event) =>
            event.stopPropagation()
          }
        />
      </div>

      <div>
        <DmsLexisIcon type="folder" />
      </div>

      <div className="min-w-0">
        <div className="truncate font-black text-[#15172f]">
          {row.title}
        </div>
        <div className="truncate text-[11px] font-bold uppercase text-[#6e7391]">
          {row.kind === "project" ? "DMS-PRJ" : "DMS-TYPE"} · {row.subtitle}
        </div>
      </div>

      <div>
        <span className="bg-[#252a55] px-2 py-1 text-[10px] font-black text-white">
          ACTIVE
        </span>
      </div>

      <div className="font-black text-[#15172f]">
        {row.count}
      </div>

      <div className="font-bold text-[#6e7391]">
        {bytesToSize(row.size)}
      </div>

      <div className="text-xs font-bold text-[#6e7391]">
        {safeDate(row.latest)}
      </div>

      <div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="bg-[#11132b] px-3 py-2 text-xs font-black text-white"
        >
          Abrir
        </button>
      </div>
    </div>
  );
}

function DmsLexisDocumentRow({ row, selected, onSelect, downloadingId, onDownload }) {
  const doc = row.doc;
  const id = getDocId(doc);

  return (
    <div
      onClick={onSelect}
      className={`grid cursor-pointer grid-cols-[34px_50px_minmax(260px,1fr)_110px_90px_120px_130px_110px] items-center border-b border-[#d8dcef] px-5 py-4 text-sm transition ${
        selected ? "bg-[#dfe4ff]" : "bg-[#eef0fb] hover:bg-[#e5e8f7]"
      }`}
    >
      <div>
        <input type="checkbox" checked={selected} readOnly />
      </div>

      <div>
        <DmsLexisIcon type={getTypeLabel(doc)} />
      </div>

      <div className="min-w-0">
        <div className="truncate font-black text-[#15172f]">
          {getDocFileName(doc)}
        </div>
        <div className="truncate text-[11px] font-bold uppercase text-[#6e7391]">
          {id || "-"}
        </div>
      </div>

      <div>
        <StatusPill status={doc.status} />
      </div>

      <div className="font-black text-[#15172f]">
        v1.0
      </div>

      <div className="truncate font-bold text-[#6e7391]">
        Sistema
      </div>

      <div className="text-xs font-bold text-[#6e7391]">
        {safeDate(getDocDate(doc))}
      </div>

      <div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDownload(doc);
          }}
          disabled={downloadingId === id}
          className="bg-[#11132b] px-3 py-2 text-xs font-black text-white disabled:opacity-50"
        >
          {downloadingId === id ? "..." : "Abrir"}
        </button>
      </div>
    </div>
  );
}

function DmsLexisIcon({ type }) {
  const label = normalize(type).toUpperCase();

  if (label === "FOLDER") {
    return (
      <div className="relative h-7 w-8 border border-[#9ca3c7] bg-[#dce2f5]">
        <div className="absolute left-0 top-0 h-2 w-5 bg-[#bcc7ec]" />
      </div>
    );
  }

  if (label === "PDF") {
    return <div className="text-xs font-black text-[#cf2f46]">PDF</div>;
  }

  if (label === "EXCEL") {
    return <div className="text-xs font-black text-[#1c8c61]">XLS</div>;
  }

  if (label === "WORD") {
    return <div className="text-xs font-black text-[#315fc4]">DOC</div>;
  }

  return <div className="text-xs font-black text-[#6e7391]">FILE</div>;
}

function DmsLexisProperties({
  item,
  docs,
  driveProject,
  driveDocType,
  totalSize,
  pdfCount,
  onDownload,
  downloadingId,
}) {
  if (!item) {
    return (
      <div className="p-4 text-sm font-bold text-[#6e7391]">
        Selecciona un documento o carpeta para ver el detalle.
      </div>
    );
  }

  const isDoc = item.kind === "document";
  const doc = item.doc;
  const title = isDoc ? getDocFileName(doc) : item.title;
  const id = isDoc ? getDocId(doc) : item.key;
  const size = isDoc ? getDocSize(doc) : item.size;
  const date = isDoc ? getDocDate(doc) : item.latest;
  const projectName = isDoc ? doc.projectName : driveProject;
  const docType = isDoc ? doc.docType : driveDocType;

  return (
    <div>
      <div className="border-b border-[#d8dcef] px-5 py-4">
        <DmsProp label={isDoc ? "Archivo" : "Carpeta"} value={title} />
        <DmsProp label="Tamaño" value={bytesToSize(size)} />
        <DmsProp
          label="Ruta"
          value={`/root/${projectName || "todos"}/${docType || "indice"}`}
        />
        <DmsProp label="Fecha" value={safeDate(date)} />
      </div>

      <div className="border-b border-[#d8dcef] px-5 py-4">
        <div className="mb-3 text-[10px] font-black uppercase tracking-[0.18em] text-[#6e7391]">
          Identificación
        </div>

        <div className="bg-[#e2e5f4] px-3 py-3 text-xs font-bold text-[#15172f]">
          {id || "DMS-OBJECT"}
        </div>

        <div className="mt-4 flex items-center justify-between text-xs font-bold">
          <span className="uppercase tracking-[0.14em] text-[#6e7391]">Estatus OCR</span>
          <span className="text-[#1d4ed8]">Procesado</span>
        </div>

        <div className="mt-4 flex items-center justify-between text-xs font-bold">
          <span className="uppercase tracking-[0.14em] text-[#6e7391]">Clasificación</span>
          <span className="text-[#b91c1c]">Interno</span>
        </div>
      </div>

      <div className="border-b border-[#d8dcef] px-5 py-4">
        <div className="mb-3 text-[10px] font-black uppercase tracking-[0.18em] text-[#6e7391]">
          Resumen
        </div>

        <DmsProp label="Proyecto" value={projectName || driveProject || "Todos"} />
        <DmsProp label="Tipo" value={docType || driveDocType || "Índice"} />
        <DmsProp label="PDF" value={pdfCount} />
        <DmsProp label="Total" value={bytesToSize(totalSize)} />
      </div>

      <div className="p-4">
        {isDoc ? (
          <button
            type="button"
            onClick={() => onDownload(doc)}
            disabled={downloadingId === getDocId(doc)}
            className="w-full bg-[#11132b] px-5 py-3 text-sm font-black text-white disabled:opacity-50"
          >
            {downloadingId === getDocId(doc) ? "Abriendo..." : "Abrir documento"}
          </button>
        ) : (
          <div className="w-full bg-[#11132b] px-5 py-3 text-center text-sm font-black text-white">
            {item.count || docs.length} elementos
          </div>
        )}
      </div>
    </div>
  );
}
function DmsProp({ label, value }) {
  return (
    <div className="mb-3 grid grid-cols-[92px_1fr] gap-3 text-xs">
      <div className="font-black uppercase tracking-[0.14em] text-[#6e7391]">
        {label}
      </div>
      <div className="break-words font-bold text-[#15172f]">
        {value || "-"}
      </div>
    </div>
  );
}
function ListView({ docs, downloadingId, onDownload }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1050px] text-sm">
        <thead className="bg-slate-950 text-white">
          <tr>
            <Th>Documento</Th>
            <Th>Clasificación</Th>
            <Th>Fecha</Th>
            <Th>Estatus</Th>
            <Th>Tamaño</Th>
            <Th align="right">Acciones</Th>
          </tr>
        </thead>

        <tbody>
          {docs.map((doc, idx) => {
            const id = getDocId(doc);
            const name = getDocFileName(doc);
            const contentType = doc.contentType || doc.s3ContentType || "-";

            return (
              <tr
                key={id || idx}
                className={`border-b border-slate-100 transition hover:bg-indigo-50/40 ${
                  idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                }`}
              >
                <td className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    <FileIcon doc={doc} />

                    <div className="min-w-0">
                      <div className="max-w-none break-words font-black leading-5 text-slate-950">
                        {name}
                      </div>

                      <div className="mt-1 text-xs font-medium text-slate-400">
                        ID: {id || "-"}
                      </div>

                      <div className="mt-1 text-xs text-slate-400">
                        {contentType}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4">
                  <div className="font-black text-slate-700">
                    {doc.projectName || "-"}
                  </div>

                  <div className="mt-1 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600">
                    {doc.docType || "SIN_TIPO"}
                  </div>
                </td>

                <td className="px-5 py-4 font-bold text-slate-600">
                  {safeDate(getDocDate(doc))}
                </td>

                <td className="px-5 py-4">
                  <StatusPill status={doc.status} />
                </td>

                <td className="px-5 py-4 font-bold text-slate-600">
                  {bytesToSize(getDocSize(doc))}
                </td>

                <td className="px-5 py-4">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => onDownload(doc)}
                      disabled={downloadingId === id}
                      className="rounded-2xl bg-slate-950 px-4 py-2 text-xs font-black text-white transition hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {downloadingId === id ? "Abriendo..." : "Ver / Descargar"}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GridView({ docs, downloadingId, onDownload }) {
  return (
    <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2 xl:grid-cols-3">
      {docs.map((doc, idx) => {
        const id = getDocId(doc);
        const name = getDocFileName(doc);

        return (
          <article
            key={id || idx}
            className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-100 hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <FileIcon doc={doc} />

                <div className="min-w-0">
                  <h3 className="break-words text-sm font-black leading-5 text-slate-950">
                    {name}
                  </h3>

                  <p className="mt-1 break-words text-xs font-medium text-slate-400">
                    ID: {id || "-"}
                  </p>
                </div>
              </div>

              <FileTypeBadge doc={doc} />
            </div>

            <div className="mt-4 space-y-3 rounded-2xl bg-slate-50 p-4">
              <MetaRow label="Proyecto" value={doc.projectName || "-"} />
              <MetaRow label="Tipo" value={doc.docType || "-"} />
              <MetaRow label="Fecha" value={safeDate(getDocDate(doc))} />
              <MetaRow label="Tamaño" value={bytesToSize(getDocSize(doc))} />
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <StatusPill status={doc.status} />

              <button
                type="button"
                onClick={() => onDownload(doc)}
                disabled={downloadingId === id}
                className="rounded-2xl bg-slate-950 px-4 py-2 text-xs font-black text-white transition hover:bg-indigo-700 disabled:opacity-50"
              >
                {downloadingId === id ? "Abriendo..." : "Ver"}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 text-xs">
      <span className="font-black uppercase tracking-[0.12em] text-slate-400">
        {label}
      </span>

      <span className="text-right font-black text-slate-700">
        {value}
      </span>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="p-8">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="h-32 animate-pulse rounded-[24px] border border-slate-100 bg-slate-50"
          />
        ))}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-50 text-xl font-black text-slate-400">
        DOC
      </div>

      <h3 className="mt-4 text-lg font-black text-slate-950">
        No hay documentos con los filtros seleccionados
      </h3>

      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
        Ajusta el proyecto, tipo documental, estatus, fecha o búsqueda para ampliar
        los resultados disponibles.
      </p>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-5 py-4 text-[11px] font-black uppercase tracking-[0.16em]"
      style={{ textAlign: align }}
    >
      {children}
    </th>
  );
}


























